import {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react';

type DocumentVisibilityState = typeof document.visibilityState;

const DocumentVisibilityContext =
  createContext<DocumentVisibilityState>('visible');

export const DocumentVisibilityProvider: FC<PropsWithChildren> = ({
  children,
}) => {
  const [visibility, setVisibility] =
    useState<DocumentVisibilityState>('visible');

  useEffect(() => {
    const handleVisibilityChange = () => {
      setVisibility(document.visibilityState);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return (
    <DocumentVisibilityContext.Provider value={visibility}>
      {children}
    </DocumentVisibilityContext.Provider>
  );
};

/**
 * Hook to provide a value matching the `document.visibilityState` value.
 * Initially to be used in the AppContext for `ui` and in the `preview` route
 * for the `site-builder`.
 */
export const useDocumentVisibility = (): DocumentVisibilityState => {
  const context = useContext(DocumentVisibilityContext);
  if (context === undefined) {
    throw new Error(
      'useDocumentVisibility must be used within a DocumentVisibilityProvider'
    );
  }
  return context;
};
